import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtMoney } from "@/lib/apiClient";
import { PageHeader, BcRibbonGroup, BcRibbonBtn } from "@/components/Shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowsClockwise, ChartBar } from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, PieChart, Pie, Cell,
} from "recharts";

const CC_PALETTE = ["#2C7A7B", "#2E75B6", "#EA580C", "#A4262C", "#8A6E00", "#107C10", "#5C2D91"];
const CHART_TOOLTIP_STYLE = { borderRadius: 2, border: "1px solid #EDEBE9", fontSize: 12 };
const fmtTooltipMoney = (v) => fmtMoney(v);

export default function ProfitLoss() {
  const today = new Date().toISOString().slice(0, 10);
  const yearStart = today.slice(0, 4) + "-04-01";
  const [from, setFrom] = useState(yearStart);
  const [to, setTo] = useState(today);
  const [summary, setSummary] = useState(null);
  const [ccData, setCcData] = useState(null);

  const load = useCallback(async () => {
    const [s, c] = await Promise.all([
      api.get("/dashboard/summary"),
      api.get("/finance/pnl-by-cost-centre", { params: { from_date: from, to_date: to } }),
    ]);
    setSummary(s.data);
    setCcData(c.data);
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  if (!summary || !ccData) return <div className="p-8 text-slate-500">Loading…</div>;

  const k = summary.kpis;
  const margin = k.total_revenue > 0 ? (k.profit / k.total_revenue) * 100 : 0;

  return (
    <div data-testid="profit-page">
      <PageHeader
        title="Profit &amp; Loss"
        module="Finance · FI"
        subtitle="Full P&L statement with cost-centre breakdown, monthly trend and margins."
        ribbon={
          <BcRibbonGroup label="Process">
            <BcRibbonBtn icon={ArrowsClockwise} label="Refresh" onClick={load} testid="ribbon-refresh" />
          </BcRibbonGroup>
        }
      />
      <div className="p-6 space-y-5">

        <Tabs defaultValue="summary" data-testid="pnl-tabs">
          <TabsList>
            <TabsTrigger value="summary" data-testid="tab-pnl-summary">Summary</TabsTrigger>
            <TabsTrigger value="cc" data-testid="tab-pnl-cost-centre">By Cost Centre</TabsTrigger>
            <TabsTrigger value="trend" data-testid="tab-pnl-trend">Monthly Trend</TabsTrigger>
          </TabsList>

          {/* ---- Summary ---- */}
          <TabsContent value="summary" className="mt-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <KpiCard label="Revenue" value={fmtMoney(k.total_revenue)} sub="Payments received + direct sales" tone="rev" />
              <KpiCard label="Expenses" value={fmtMoney(k.total_expenses)} sub="Overhead + material purchases" tone="exp" />
              <KpiCard label="Net Profit" value={fmtMoney(k.profit)} sub={`Margin: ${margin.toFixed(1)}%`} tone="profit" />
            </div>

            <div className="bc-card overflow-hidden mt-5" data-testid="profit-table">
              <div className="px-4 py-3 border-b border-[#EDEBE9] bg-[#FAF9F8]">
                <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[#605E5C]">Statement</div>
                <div className="font-display text-[15px] font-semibold mt-0.5 text-[#201F1E]">Line-by-line</div>
              </div>
              <table className="w-full text-[13px]">
                <tbody className="font-mono-num">
                  <tr className="border-b border-[#EDEBE9]"><td className="p-3 text-[#605E5C]">Total Revenue</td><td className="p-3 text-right font-semibold text-emerald-700">{fmtMoney(k.total_revenue)}</td></tr>
                  <tr className="border-b border-[#EDEBE9]"><td className="p-3 text-[#605E5C]">− Total Expenses</td><td className="p-3 text-right font-semibold text-red-700">-{fmtMoney(k.total_expenses)}</td></tr>
                  <tr className="border-b border-[#EDEBE9]"><td className="p-3 text-[#605E5C]">Outstanding receivables</td><td className="p-3 text-right">{fmtMoney(k.outstanding)}</td></tr>
                  <tr className="bg-[#FAF9F8]"><td className="p-4 font-display font-bold text-[15px]">= Net Profit</td><td className="p-4 text-right font-display font-bold text-[15px] text-[#EA580C]">{fmtMoney(k.profit)}</td></tr>
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* ---- By Cost Centre ---- */}
          <TabsContent value="cc" className="mt-5" data-testid="pnl-cc-panel">
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <div>
                <Label className="label-caps mb-1 block">From</Label>
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" />
              </div>
              <div>
                <Label className="label-caps mb-1 block">To</Label>
                <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
              <KpiCard label="Revenue" value={fmtMoney(ccData.revenue)} sub={`${from} → ${to}`} tone="rev" />
              <KpiCard label="Expenses" value={fmtMoney(ccData.expense_total)} sub="Across all cost centres" tone="exp" />
              <KpiCard label="Profit" value={fmtMoney(ccData.profit)} sub={`Margin: ${ccData.margin_pct}%`} tone="profit" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="bc-card lg:col-span-2 overflow-hidden" data-testid="pnl-cc-table">
                <div className="px-4 py-3 border-b border-[#EDEBE9] bg-[#FAF9F8]">
                  <div className="font-display text-[14px] font-semibold text-[#201F1E]">Expenses by Cost Centre</div>
                </div>
                <table className="w-full text-[13px]">
                  <thead className="bg-[#FAF9F8]">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold text-[11.5px] text-[#201F1E]">Code</th>
                      <th className="text-left px-4 py-2 font-semibold text-[11.5px] text-[#201F1E]">Cost Centre</th>
                      <th className="text-left px-4 py-2 font-semibold text-[11.5px] text-[#201F1E]">Kind</th>
                      <th className="text-right px-4 py-2 font-semibold text-[11.5px] text-[#201F1E]">Amount</th>
                      <th className="text-right px-4 py-2 font-semibold text-[11.5px] text-[#201F1E]">% of Expenses</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ccData.expenses_by_cost_centre.map((b) => (
                      <tr key={b.code} className="border-t border-[#EDEBE9]">
                        <td className="px-4 py-2 font-mono-num text-[#605E5C]">{b.code}</td>
                        <td className="px-4 py-2 font-semibold text-[#201F1E]">{b.name}</td>
                        <td className="px-4 py-2 text-[11px] uppercase tracking-widest text-[#605E5C]">{b.kind}</td>
                        <td className="px-4 py-2 text-right font-mono-num font-semibold">{fmtMoney(b.amount)}</td>
                        <td className="px-4 py-2 text-right font-mono-num">{b.pct_of_expenses}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bc-card p-4">
                <div className="font-display text-[14px] font-semibold text-[#201F1E] mb-2">Expense Mix</div>
                <div style={{ width: "100%", height: 260 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={ccData.expenses_by_cost_centre.filter((b) => b.amount > 0)}
                        dataKey="amount"
                        nameKey="name"
                        outerRadius={80}
                        innerRadius={45}
                        label={(e) => `${e.name} ${e.pct_of_expenses}%`}
                      >
                        {ccData.expenses_by_cost_centre.map((b) => (
                          <Cell key={b.code} fill={CC_PALETTE[ccData.expenses_by_cost_centre.indexOf(b) % CC_PALETTE.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={fmtTooltipMoney} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ---- Monthly Trend ---- */}
          <TabsContent value="trend" className="mt-5">
            <div className="bc-card p-5">
              <div className="flex items-center gap-2 mb-4">
                <ChartBar size={16} className="text-[#2E75B6]" />
                <div className="font-display text-[14px] font-semibold text-[#201F1E]">Monthly Revenue / Expense / Profit</div>
              </div>
              <div style={{ width: "100%", height: 360 }}>
                <ResponsiveContainer>
                  <BarChart data={summary.monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EDEBE9" />
                    <XAxis dataKey="month" stroke="#605E5C" fontSize={11} />
                    <YAxis stroke="#605E5C" fontSize={11} />
                    <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={fmtTooltipMoney} />
                    <Legend />
                    <Bar dataKey="revenue" fill="#107C10" />
                    <Bar dataKey="expense" fill="#A4262C" />
                    <Bar dataKey="profit" fill="#2E75B6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, tone }) {
  const tones = {
    rev:    { bg: "bg-white",         val: "text-emerald-700",  border: "border-emerald-200" },
    exp:    { bg: "bg-white",         val: "text-red-700",      border: "border-red-200" },
    profit: { bg: "bg-[#0F172A]",     val: "text-[#EA580C]",    border: "border-[#0F172A]" },
  };
  const t = tones[tone] || tones.rev;
  const isDark = tone === "profit";
  return (
    <div className={`border p-5 rounded-[2px] ${t.bg} ${t.border}`}>
      <div className={`text-[10px] uppercase tracking-[0.14em] font-semibold ${isDark ? "text-slate-400" : "text-[#605E5C]"}`}>{label}</div>
      <div className={`font-display text-[28px] font-bold font-mono-num mt-2 ${t.val}`}>{value}</div>
      <div className={`text-[11px] mt-1 ${isDark ? "text-slate-400" : "text-[#605E5C]"}`}>{sub}</div>
    </div>
  );
}
